export interface PromoWindow {
  id?: string;
  dowMask: number;
  localStartTime: string;
  localEndTime: string;
  /** Set on a revenue campaign's code: it applies on this local date only. */
  localDate?: string | null;
  /** Latest local start time a slot may have to qualify. */
  latestStartTime?: string | null;
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_MASK = 0b0111110; // Mon-Fri = bits 1..5 = 62
const WEEKENDS_MASK = 0b1000001; // Sun + Sat = bits 0 and 6 = 65
const ANY_DAY_MASK = 0b1111111;  // 127

export function dowMaskFromIndices(indices: number[]): number {
  let mask = 0;
  for (const i of indices) {
    if (i >= 0 && i <= 6) mask |= (1 << i);
  }
  return mask;
}

export function indicesFromDowMask(mask: number): number[] {
  const out: number[] = [];
  for (let i = 0; i <= 6; i += 1) {
    if ((mask >> i) & 1) out.push(i);
  }
  return out;
}

function formatLocalTime12h(hhmm: string): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const meridiem = h < 12 ? 'AM' : 'PM';
  const displayH = h % 12 === 0 ? 12 : h % 12;
  const displayM = m === 0 ? '' : `:${String(m).padStart(2, '0')}`;
  return `${displayH}${displayM} ${meridiem}`;
}

function formatDowMask(mask: number): string {
  if (mask === ANY_DAY_MASK) return 'Any day';
  if (mask === WEEKDAYS_MASK) return 'Weekdays';
  if (mask === WEEKENDS_MASK) return 'Weekends';
  return indicesFromDowMask(mask).map((i) => DOW_LABELS[i]).join('/');
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatLocalDate(localDate: string): string {
  const [year, month, day] = localDate.split('-').map(Number);
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${DOW_LABELS[dow]} ${MONTH_LABELS[month - 1]} ${day}`;
}

export function formatPromoWindow(window: PromoWindow): string {
  const day = window.localDate ? formatLocalDate(window.localDate) : formatDowMask(window.dowMask);
  if (window.latestStartTime) {
    // Stored as the last qualifying minute (10:59); shown as the block's end.
    const [h, m] = window.latestStartTime.split(':').map(Number);
    const blockEnd = m === 59 ? `${String((h + 1) % 24).padStart(2, '0')}:00` : window.latestStartTime;
    return `${day}, times starting ${formatLocalTime12h(window.localStartTime)}–${formatLocalTime12h(blockEnd)}`;
  }
  return `${day} ${formatLocalTime12h(window.localStartTime)}–${formatLocalTime12h(window.localEndTime)}`;
}

export function formatPromoWindows(windows: PromoWindow[] | null | undefined): string {
  if (!windows || windows.length === 0) return '';
  return windows.map(formatPromoWindow).join(' or ');
}

export { WEEKDAYS_MASK, WEEKENDS_MASK, ANY_DAY_MASK, DOW_LABELS };
